import { useEffect, useState } from 'react';
import { useAuth } from '@/app/providers/AuthProvider';
import { calcExpiry } from '@/shared/format/date';
import { normalizePlan, planById } from '@/shared/format/plan';

interface Props {
  onClick: () => void;
}

interface CachedTenant {
  PlanExpiresOn?: string | null;
  TrialEndsOn?: string | null;
}

function readTenantCache(): CachedTenant | null {
  try {
    const raw = localStorage.getItem('tsetsu.tenant.json');
    return raw ? (JSON.parse(raw) as CachedTenant) : null;
  } catch {
    return null;
  }
}

export function PlanBadge({ onClick }: Props) {
  const { snapshot } = useAuth();
  const planId = normalizePlan(snapshot.plan);
  const meta = planById(planId);
  // Tenant cache өөрчлөгдөхөд UI rerender хийхийн тулд timestamp-аар track хийнэ.
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'tsetsu.tenant.json') setTick((x) => x + 1);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const cached = readTenantCache();
  const ends =
    planId === 'free' ? cached?.TrialEndsOn ?? cached?.PlanExpiresOn : cached?.PlanExpiresOn;
  const exp = calcExpiry(ends ?? null);

  let expirySpan = null;
  let title = meta.label;
  let icon = 'gem';
  if (exp) {
    const text =
      exp.severity === 'expired'
        ? 'дууссан'
        : exp.daysLeft === 0
          ? 'өнөөдөр дуусна'
          : `${exp.daysLeft} хоног`;
    expirySpan = (
      <span className={`ts-plan-expiry ts-sev-${exp.severity}`}> · {text}</span>
    );
    title = `${meta.label} — ${exp.isoDate} хүртэл хүчинтэй`;
    if (exp.severity === 'critical' || exp.severity === 'expired') {
      icon = 'exclamation-triangle-fill';
    }
  }

  return (
    <button
      key={tick}
      type="button"
      className="ts-btn-outline"
      title={title}
      onClick={onClick}
      style={{
        padding: '5px 11px',
        fontSize: '0.75rem',
        borderColor: `${meta.color}66`,
        color: meta.color,
        background: `${meta.color}14`,
      }}
    >
      <i className={`bi bi-${icon}`} /> {meta.label}
      {expirySpan}
    </button>
  );
}
